
export interface NightAuditStep {
    id: number;
    title: string;
    short: string;
    desc: string;
    items: NightAuditItem[];
}

export interface NightAuditItem {
    l: string;
    n?: string;
    subs?: string[];
    copy?: boolean;
}

export const NIGHT_AUDIT_STEPS: NightAuditStep[] = [
  { id:1, title:"Preparation", short:"Prep", desc:"Verify all arrivals, wake-up calls, pending tasks, and Ullivik rooms before the audit begins.", items:[
    {l:"Verify all arrivals are complete",n:"Check keys, Reg Cards, ID photocopies, and confirm with B-shift receptionist. No guest should be missing a check-in."},
    {l:"Review outstanding keys with the arrival list",n:"Cross-reference physical keys against the arrival list. A missing key may indicate a missing check-in.",subs:[
      "If a key is missing, verify whether the corresponding check-in was completed in the system.",
      "If check-in is missing, complete it before proceeding."
    ]},
    {l:"Review Reg card signatures on the arrival list",n:"Go through each Reg card and check for a signature.",subs:[
      "If a Reg card is signed, it confirms the guest was physically present — a missing check-in in the system must be corrected."
    ]},
    {l:"Review any payment due",n:"Review all in-house guests (non-Ullivik) for any outstanding balances needing collection."},
    {l:"Check for arrivals on canceled flights (Airway Interline / Air Canada)",n:"Visit the airline website to verify flight cancellations.",subs:[
      "Check the Airway Interline and Air Canada websites for canceled inbound flights.",
      "If a flight was canceled, identify the affected reservation.",
      "Prepare the reservation and have keys ready for when the guest arrives."
    ]},
    {l:"Check system milestones",n:"Review team chat, document tray, or verbal notes."},
    {l:"Verify wake-up calls",n:"Compare tomorrow's departures with the printed sheet and review expected departures for the next day."},
    {l:"Check for emergency requests",n:"Scan for any urgent or emergency notes left by the previous shift."},
    {l:"Review any pending arrivals or departures",n:"Ensure no more expected arrivals; mark no-shows, especially for Ullivik.",subs:[
      "Mark no-shows accordingly — especially for Ullivik. Verify after all planes have arrived (AirInuit or Flight Montreal). Keep for Ullivik / Assigned Names.",
      "Check if there are any add-on guests on the tray and that the guest count is correct."
    ]},
    {l:"Check Ullivik rooms, verify rates; send room count to Microsoft Teams",n:"Verify with in-house list report (count – rate).",subs:[
      "Any late departure: keep in-house until night close, but without meal.",
      "Any late arrival: fix meal to be posted on 1st night.",
      "Verify with in-house list report (count – rate)."
    ]},
    {l:"Review regular guest arrivals",n:"Mark no-shows per expected arrival time. Check in if paid; cancel if not paid."},
    {l:"Review Day Use",n:"Print transaction details report, check day use posted, then verify checkout and posted fees."},
    {l:"Print screen Ullivik room assignments",n:"Kept for Ullivik or with individual names as applicable."},
    {l:"Perform check-in for Ullivik no-shows",n:"Complete all four sub-steps for each no-show room.",subs:[
      "Check in using the 'collection' option only (Ullivik).",
      "Write note: No Show.",
      "Remove recurrent meals.",
      "Set stay to one night."
    ]},
    {l:"Update in-house list after adjusting arrivals/departures",n:"Ensure the list reflects all changes made during preparation."}
  ]},
  { id:2, title:"Pre-Audit Reports", short:"Reports", desc:"Print all required reports before running the night audit. Label clearly for reference.", items:[
    {l:"Print Hotel Journal Summary",n:"Mark clearly as 'Before Audit' — this is the pre-audit baseline, distinct from the post-audit version."},
    {l:"Print Hotel Journal Details",n:"Print all three sub-reports.",subs:[
      "Cash sub-report.",
      "Debit Card sub-report.",
      "Cash Paid Out report."
    ]},
    {l:"Get payment report from back office desk drawer",n:"Verify receipts and staple to reports after review.",subs:[
      "Make sure all receipts are present.",
      "Staple receipts with reports after review."
    ]}
  ]},
  { id:3, title:"Bank Reconciliation", short:"Reconcile", desc:"Verify credit/debit totals, close the batch, and print confirmation from the payment terminal.", items:[
    {l:"Review Hotel Journal Summary",n:"Cross-check totals before proceeding to the payment terminal."},
    {l:"Review POS machine — close batch if not already done",n:"If the batch has not been closed, close it now.",subs:[
      "Navigate: OK → UP → 7 → OK. If not closed, do it."
    ]},
    {l:"Navigate to Top Bar → Dollar on the Net",n:"Access the reconciliation screen in the payment system."},
    {l:"Assign date (top left side)",n:"Ensure the date is set correctly before pulling totals."},
    {l:"Verify totals for credit cards and debit",n:"Confirm totals match the Hotel Journal Summary figures."},
    {l:"Print detailed reports",n:"Keep a printed copy for the night audit folder."},
    {l:"Close batch and print confirmation",n:"This finalizes the day's payment processing."},
    {l:"Log out of the payment system",n:"Always log out to secure the terminal after reconciliation."}
  ]},
  { id:4, title:"Night Audit Closing", short:"Closing", desc:"Close the shift, run the audit, print all required reports, and process Ullivik checkouts.", items:[
    {l:"Close shift after midnight and save",n:"Do not run the audit before midnight. Save the shift before proceeding."},
    {l:"Run Night Audit from system RUN menu",n:"This processes all transactions and rolls the date forward."},
    {l:"Preview Night Audit reports and print",n:"Print all four required reports.",subs:[
      "Hotel Journal Summary — mark as 'After Audit'.",
      "Hotel Statistics.",
      "Final Transactions.",
      "Guest Ledger."
    ]},
    {l:"Refund outstanding balances where applicable",n:"Process any refunds pending for departing guests."},
    {l:"Review Ullivik no-shows and adjust balances accordingly",n:"Confirm all Ullivik no-show adjustments are correct."},
    {l:"Perform check-out — rooms kept for Ullivik",n:"Complete all three sub-steps for Ullivik group rooms.",subs:[
      "Post adjustment room charges on guest folio.",
      "Post total amount on the open Group Master (comment: No rooms – Date) from hotel journal summary total room adjustment.",
      "Check out."
    ]},
    {l:"Perform check-out — Assigned Names rooms",n:"Complete both sub-steps for individually assigned rooms.",subs:[
      "Post payment room charges on guest folio.",
      "Check out."
    ]},
    {l:"Review Ullivik via Hotel Journal Summary",n:"Verify the rate is correct after checkout.",subs:[
      "Divide adjustment total by room count — target rate: $149."
    ]}
  ]},
  { id:5, title:"Daily Reports", short:"Daily", desc:"Generate occupancy snapshots, revenue indexes, and company statistics for the owners.", items:[
    {l:"Print Occupancy Snapshot",n:"Covering full month, showing past 7 days and upcoming 7 days."},
    {l:"Open Shift 4 and set cash float to zero",n:"Reset the float before generating the revenue report."},
    {l:"Generate Daily Night Audit Report (with audit date)",n:"Use the correct audit date when generating this report."},
    {l:"Generate Daily Revenue Index using Occupancy Snapshot data",n:"Fill in the revenue index fields from the Occupancy Snapshot figures."},
    {l:"Fill 'Other Revenue' fields",n:"Enter amounts for all applicable categories.",subs:[
      "Damage, Misc, Food, Lounge Food, Meeting Room, Parking, Vending, Laundry."
    ]},
    {l:"Compare with Net Revenue",n:"Subtract Other Revenue + Gratuity + Day Use Fees from Total Revenue."},
    {l:"Send Daily Revenue Index via email to owners",n:"Send the completed report to all required recipients."}
  ]},
  { id:6, title:"Housekeeping", short:"HK", desc:"Return no-show rooms to housekeeping and distribute the check-off report.", items:[
    {l:"Return no-show rooms to Housekeeping as 'ready'",n:"Update room status in the system so housekeeping can service them."},
    {l:"Print Check-Off report for Housekeeping (5:00 AM)",n:"Distribute to the housekeeping team at the start of their shift."}
  ]},
  { id:7, title:"Finalization", short:"Final", desc:"Organize all reports, reconcile cash, prepare keys, and send the morning Teams report.", items:[
    {l:"Gather and organize all reports in the Night Audit folder",n:"File all printed reports correctly — includes reconciliation and folder filing.",subs:[
      "Cash Report: reconcile cash, debit, and cash paid out with payment terminal receipts.",
      "Store reports in the Cash Report folder.",
      "Generate In-House List and Final Transactions from FTC & In-House folder."
    ]},
    {l:"Make Ullivik check-out keys",n:"Prepare and set aside room keys for all Ullivik check-outs scheduled for the next day, so they are ready at the front desk."},
    {l:"Write Fast Report on Teams",n:"Post all seven data points to Microsoft Teams for the management morning briefing.",copy:true,subs:[
      "Date.",
      "No. of in-house rooms including Day Use.",
      "No. of complimentary rooms.",
      "No. of Ullivik No show assigned names.",
      "No. of Keep for Ullivik No show.",
      "Regular guest no-show rooms.",
      "Change rooms."
    ]}
  ]}
];
